import React, { useEffect } from "react";
import { Shield, Info } from "lucide-react";
import MortgageChainFooter from "../../components/MortgageChainFooter/MortgageChainFooter";

export default function MortgageChainPrivacyPolicy() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const emitFormEvent = (type: "call" | "deck") => {
    window.dispatchEvent(
      new CustomEvent("mortgagechain:openForm", { detail: { type } })
    );
  };

  return (
    <div className="bg-[#0A0E27] text-white min-h-screen">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;600;700&family=Inter:wght@400;500;600&display=swap');
        * { font-family: 'Inter', sans-serif; }
        h1, h2, h3 { font-family: 'Space Grotesk', sans-serif; }
        .glassmorphic { background: rgba(26,31,58,0.8); backdrop-filter: blur(20px); border: 1px solid rgba(0,212,255,0.2); }
        .gradient-text { background: linear-gradient(90deg,#00D4FF 0%,#8B5CF6 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
      `}</style>

      <section className="relative pt-28 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/30 backdrop-blur-sm mb-6">
              <Shield className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-semibold text-cyan-400">
                Privacy Policy
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-4">
              Privacy <span className="gradient-text">Policy</span>
            </h1>
            <p className="text-gray-400 text-lg">
              Last Updated: January 15, 2025
            </p>
          </div>

          <div className="glassmorphic rounded-2xl p-8 mb-8">
            <div className="bg-cyan-500/10 border-l-4 border-cyan-500 rounded-lg p-6 mb-8">
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                <Info className="w-5 h-5 text-cyan-400" />
                Your Privacy Matters
              </h3>
              <p className="text-gray-300">
                Crypsey is committed to protecting your privacy and handling
                your personal information with care and transparency. This
                Privacy Policy explains how we collect, use, disclose, and
                safeguard your information in compliance with Canada&apos;s
                Personal Information Protection and Electronic Documents Act
                (PIPEDA) and other applicable privacy laws.
              </p>
            </div>

            <div className="space-y-8">
              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  1. About Crypsey
                </h2>
                <p className="text-gray-300 mb-4">
                  <strong className="text-white">Crypsey Corp</strong>{" "}
                  (&quot;Crypsey,&quot; &quot;we,&quot; &quot;us,&quot; or
                  &quot;our&quot;) operates a mortgage data analytics platform
                  that aggregates anonymized mortgage performance data and
                  provides insights to financial institutions, investors, and
                  fintech companies.
                </p>
                <p className="text-gray-300 mb-2">
                  <strong className="text-white">Contact Information:</strong>
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">Company:</strong> Crypsey
                    Inc.
                  </li>
                  <li>
                    <strong className="text-white">Email:</strong>{" "}
                    <a
                      href="mailto:patrick@crypsey.com"
                      className="text-cyan-400 hover:text-cyan-300"
                    >
                      patrick@crypsey.com
                    </a>
                  </li>
                  <li>
                    <strong className="text-white">Address:</strong> Toronto,
                    Canada
                  </li>
                  <li>
                    <strong className="text-white">Website:</strong>{" "}
                    <a
                      href="https://www.crypsey.com"
                      className="text-cyan-400 hover:text-cyan-300"
                    >
                      www.crypsey.com
                    </a>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  2. Scope of This Policy
                </h2>
                <p className="text-gray-300 mb-3">
                  This Privacy Policy applies to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Our website (www.crypsey.com)</li>
                  <li>Our API and data platform services</li>
                  <li>Communications with customers and partners</li>
                  <li>Any other services we provide</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  3. Information We Collect
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  3.1 Customer Information (Platform Users)
                </h3>
                <p className="text-gray-300 mb-3">
                  When you register for or use our platform, we collect:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">Account Information:</strong>{" "}
                    Name, email address, phone number, company name, job title
                  </li>
                  <li>
                    <strong className="text-white">Authentication Data:</strong>{" "}
                    Username, password (encrypted), API keys
                  </li>
                  <li>
                    <strong className="text-white">Billing Information:</strong>{" "}
                    Payment method details, billing address, transaction history
                  </li>
                  <li>
                    <strong className="text-white">Usage Data:</strong> API
                    queries, reports generated, features accessed, login times
                  </li>
                  <li>
                    <strong className="text-white">Technical Data:</strong> IP
                    address, browser type, device information, operating system
                  </li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  3.2 Mortgage Data (Anonymized)
                </h3>
                <div className="bg-amber-500/10 border-l-4 border-amber-500 rounded-lg p-4 mb-4">
                  <p className="text-amber-200 font-semibold mb-2">
                    <strong>Important:</strong> We DO NOT collect personally
                    identifiable information (PII) about individual borrowers.
                    All mortgage data we receive from our data partners is fully
                    anonymized before it enters our system.
                  </p>
                </div>
                <p className="text-gray-300 mb-3">
                  The anonymized mortgage data includes:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">
                      Loan Characteristics:
                    </strong>{" "}
                    Loan amount, interest rate, loan-to-value ratio, property
                    type, postal code prefix (first 3 digits only)
                  </li>
                  <li>
                    <strong className="text-white">
                      Borrower Profile (Aggregated):
                    </strong>{" "}
                    Credit score range, employment type, debt-to-income ratio,
                    industry sector
                  </li>
                  <li>
                    <strong className="text-white">Performance Metrics:</strong>{" "}
                    Payment status, days past due, prepayment activity, monthly
                    payment history
                  </li>
                </ul>
                <p className="text-gray-300 mt-4">
                  <strong className="text-white">
                    Data Stripped from All Records:
                  </strong>{" "}
                  Names, addresses, social insurance numbers, exact property
                  locations, phone numbers, email addresses, and any other
                  personally identifiable information.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  3.3 Website Visitors
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">Analytics Data:</strong>{" "}
                    Pages viewed, time on site, referral source, geographic
                    location (city/region level)
                  </li>
                  <li>
                    <strong className="text-white">Cookies:</strong> Session
                    cookies, preference cookies, analytics cookies (see Section
                    8)
                  </li>
                  <li>
                    <strong className="text-white">Contact Forms:</strong> Name,
                    email, company, message content when you contact us
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  4. How We Use Your Information
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  4.1 Customer Information
                </h3>
                <p className="text-gray-300 mb-3">
                  We use customer information to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">Provide Services:</strong>{" "}
                    Deliver platform access, API functionality, reports, and
                    support
                  </li>
                  <li>
                    <strong className="text-white">Account Management:</strong>{" "}
                    Create and maintain your account, process payments, send
                    service updates
                  </li>
                  <li>
                    <strong className="text-white">Communication:</strong>{" "}
                    Respond to inquiries, send product updates, provide customer
                    support
                  </li>
                  <li>
                    <strong className="text-white">Improvement:</strong> Analyze
                    usage patterns to improve our platform and develop new
                    features
                  </li>
                  <li>
                    <strong className="text-white">Security:</strong> Detect
                    fraud, prevent abuse, ensure platform security
                  </li>
                  <li>
                    <strong className="text-white">Legal Compliance:</strong>{" "}
                    Comply with legal obligations, enforce our terms of service
                  </li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  4.2 Anonymized Mortgage Data
                </h3>
                <p className="text-gray-300 mb-3">
                  We use anonymized mortgage data to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Generate market analytics and benchmarking reports</li>
                  <li>Provide API responses to customer queries</li>
                  <li>Create aggregate statistics and trend analysis</li>
                  <li>Develop predictive models and analytics tools</li>
                  <li>
                    Publish industry research and market reports (aggregate data
                    only)
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  5. How We Share Your Information
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  5.1 We DO NOT Sell Personal Information
                </h3>
                <p className="text-gray-300">
                  Crypsey does not sell, rent, or trade customer personal
                  information to third parties for marketing purposes.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  5.2 Service Providers
                </h3>
                <p className="text-gray-300 mb-3">
                  We may share information with trusted service providers who
                  assist us in operating our business:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">Cloud Hosting:</strong>{" "}
                    Amazon Web Services (AWS) for infrastructure
                  </li>
                  <li>
                    <strong className="text-white">Payment Processing:</strong>{" "}
                    Stripe for payment transactions
                  </li>
                  <li>
                    <strong className="text-white">Email Services:</strong>{" "}
                    SendGrid or similar for transactional emails
                  </li>
                  <li>
                    <strong className="text-white">Analytics:</strong> Google
                    Analytics (anonymized) for website analytics
                  </li>
                  <li>
                    <strong className="text-white">Customer Support:</strong>{" "}
                    Help desk software providers
                  </li>
                </ul>
                <p className="text-gray-300 mt-4">
                  All service providers are contractually obligated to protect
                  your information and use it only for specified purposes.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  5.3 Business Transfers
                </h3>
                <p className="text-gray-300">
                  If Crypsey is involved in a merger, acquisition, or sale of
                  assets, your information may be transferred as part of that
                  transaction. We will notify you of any such change.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  5.4 Legal Requirements
                </h3>
                <p className="text-gray-300 mb-3">
                  We may disclose information when required by law, such as:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>In response to valid court orders or subpoenas</li>
                  <li>To comply with regulatory requirements</li>
                  <li>To protect our rights, property, or safety</li>
                  <li>To prevent fraud or illegal activity</li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  5.5 Anonymized Data Sharing
                </h3>
                <p className="text-gray-300">
                  We provide anonymized, aggregated mortgage data to our
                  platform customers. This data contains no personally
                  identifiable information and cannot be used to identify
                  individual borrowers.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  6. Data Security
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  6.1 Security Measures
                </h3>
                <p className="text-gray-300 mb-3">
                  We implement industry-standard security measures to protect
                  your information:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">Encryption:</strong> All data
                    transmitted over the internet uses TLS 1.3 encryption
                  </li>
                  <li>
                    <strong className="text-white">Storage:</strong> Data at
                    rest is encrypted using AES-256 encryption
                  </li>
                  <li>
                    <strong className="text-white">Access Controls:</strong>{" "}
                    Role-based access controls and multi-factor authentication
                  </li>
                  <li>
                    <strong className="text-white">Monitoring:</strong> 24/7
                    security monitoring and intrusion detection
                  </li>
                  <li>
                    <strong className="text-white">Audits:</strong> Regular
                    security audits and penetration testing
                  </li>
                  <li>
                    <strong className="text-white">Compliance:</strong> Working
                    toward SOC 2 Type II certification
                  </li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  6.2 Blockchain Verification
                </h3>
                <p className="text-gray-300">
                  We publish cryptographic hashes of our datasets to the Solana
                  blockchain monthly. This creates an immutable audit trail but
                  does not expose any actual data—only verification hashes.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  6.3 Data Breach Response
                </h3>
                <p className="text-gray-300 mb-3">
                  In the unlikely event of a data breach affecting personal
                  information, we will:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Notify affected individuals within 72 hours</li>
                  <li>
                    Report to the Office of the Privacy Commissioner of Canada
                    as required
                  </li>
                  <li>
                    Take immediate steps to contain and remediate the breach
                  </li>
                  <li>Provide guidance on protective measures you can take</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  7. Data Retention
                </h2>
                <p className="text-gray-300 mb-4">
                  We retain information for as long as necessary to provide our
                  services and comply with legal obligations:
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-4 bg-gray-800/50 font-semibold text-white">
                          Data Type
                        </th>
                        <th className="text-left py-3 px-4 bg-gray-800/50 font-semibold text-white">
                          Retention Period
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-800">
                        <td className="py-3 px-4 text-gray-300">
                          Active customer accounts
                        </td>
                        <td className="py-3 px-4 text-gray-300">
                          Duration of service + 7 years (accounting)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800">
                        <td className="py-3 px-4 text-gray-300">
                          Inactive accounts
                        </td>
                        <td className="py-3 px-4 text-gray-300">
                          2 years after last activity, then deleted
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800">
                        <td className="py-3 px-4 text-gray-300">
                          Anonymized mortgage data
                        </td>
                        <td className="py-3 px-4 text-gray-300">
                          Indefinitely (research and analytics)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800">
                        <td className="py-3 px-4 text-gray-300">Audit logs</td>
                        <td className="py-3 px-4 text-gray-300">
                          7 years (regulatory compliance)
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-300">
                          Marketing communications
                        </td>
                        <td className="py-3 px-4 text-gray-300">
                          Until you unsubscribe
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  8. Cookies and Tracking
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  8.1 Types of Cookies We Use
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">Essential Cookies:</strong>{" "}
                    Required for platform functionality (login, session
                    management)
                  </li>
                  <li>
                    <strong className="text-white">Analytics Cookies:</strong>{" "}
                    Help us understand how visitors use our website
                  </li>
                  <li>
                    <strong className="text-white">Preference Cookies:</strong>{" "}
                    Remember your settings and preferences
                  </li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  8.2 Managing Cookies
                </h3>
                <p className="text-gray-300">
                  You can control cookies through your browser settings. Note
                  that disabling essential cookies may affect platform
                  functionality.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  9. Your Privacy Rights (PIPEDA)
                </h2>

                <p className="text-gray-300 mb-4">
                  Under Canadian privacy law, you have the following rights:
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  9.1 Right to Access
                </h3>
                <p className="text-gray-300">
                  You can request a copy of the personal information we hold
                  about you.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  9.2 Right to Correction
                </h3>
                <p className="text-gray-300">
                  You can request corrections to inaccurate or incomplete
                  personal information.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  9.3 Right to Deletion
                </h3>
                <p className="text-gray-300">
                  You can request deletion of your personal information, subject
                  to legal retention requirements.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  9.4 Right to Withdraw Consent
                </h3>
                <p className="text-gray-300">
                  You can withdraw consent for us to use your information (may
                  affect service availability).
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  9.5 Right to Complaint
                </h3>
                <p className="text-gray-300">
                  You can file a complaint with the Office of the Privacy
                  Commissioner of Canada if you believe your privacy rights have
                  been violated.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  9.6 How to Exercise Your Rights
                </h3>
                <p className="text-gray-300 mb-3">
                  To exercise any of these rights, contact us at:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">Email:</strong>{" "}
                    <a
                      href="mailto:patrick@crypsey.com"
                      className="text-cyan-400 hover:text-cyan-300"
                    >
                      patrick@crypsey.com
                    </a>
                  </li>
                  <li>
                    <strong className="text-white">Subject Line:</strong>{" "}
                    &quot;Privacy Rights Request&quot;
                  </li>
                  <li>
                    <strong className="text-white">Response Time:</strong> We
                    will respond within 30 days
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  10. Children&apos;s Privacy
                </h2>
                <p className="text-gray-300">
                  Crypsey does not knowingly collect information from
                  individuals under 18 years of age. Our services are intended
                  for business and professional use only. If we discover we have
                  collected information from a minor, we will delete it
                  immediately.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  11. International Data Transfers
                </h2>
                <p className="text-gray-300">
                  Your information may be processed and stored on servers
                  located in Canada and the United States. By using our
                  services, you consent to this transfer. We ensure appropriate
                  safeguards are in place for international transfers in
                  compliance with PIPEDA.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  12. Third-Party Links
                </h2>
                <p className="text-gray-300">
                  Our website may contain links to third-party sites. We are not
                  responsible for the privacy practices of these external sites.
                  We encourage you to review their privacy policies.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  13. Marketing Communications
                </h2>
                <p className="text-gray-300 mb-3">
                  We may send you marketing emails about our services, industry
                  insights, and product updates. You can:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    Opt out at any time by clicking &quot;unsubscribe&quot; in
                    any email
                  </li>
                  <li>
                    Update your communication preferences in your account
                    settings
                  </li>
                  <li>
                    Email us at{" "}
                    <a
                      href="mailto:patrick@crypsey.com"
                      className="text-cyan-400 hover:text-cyan-300"
                    >
                      patrick@crypsey.com
                    </a>{" "}
                    to manage preferences
                  </li>
                </ul>
                <p className="text-gray-300 mt-4">
                  Note: You will still receive essential service-related emails
                  (account notifications, billing, security alerts).
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  14. Updates to This Policy
                </h2>
                <p className="text-gray-300 mb-3">
                  We may update this Privacy Policy from time to time to reflect
                  changes in our practices or legal requirements. We will:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    Post the updated policy on our website with a new &quot;Last
                    Updated&quot; date
                  </li>
                  <li>Notify customers of material changes via email</li>
                  <li>
                    Obtain consent for significant changes that affect how we
                    use your information
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  15. Questions and Complaints
                </h2>

                <div className="bg-gray-800/50 rounded-xl p-6 mt-6">
                  <h3 className="text-xl font-bold mb-4 text-white">
                    Contact Our Privacy Officer
                  </h3>
                  <p className="text-gray-300 mb-4">
                    If you have questions, concerns, or complaints about our
                    privacy practices:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>
                      <strong className="text-white">Email:</strong>{" "}
                      <a
                        href="mailto:privacy@crypsey.com"
                        className="text-cyan-400 hover:text-cyan-300"
                      >
                        privacy@crypsey.com
                      </a>
                    </li>
                    <li>
                      <strong className="text-white">Mail:</strong> Privacy
                      Officer, Crypsey Corp, Toronto, Canada
                    </li>
                    <li>
                      <strong className="text-white">Response Time:</strong> We
                      will respond within 30 days
                    </li>
                  </ul>

                  <h3 className="text-xl font-bold mt-6 mb-4 text-white">
                    Office of the Privacy Commissioner of Canada
                  </h3>
                  <p className="text-gray-300 mb-4">
                    If you are not satisfied with our response, you may contact:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>
                      <strong className="text-white">Website:</strong>{" "}
                      <a
                        href="https://www.priv.gc.ca"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-400 hover:text-cyan-300"
                      >
                        www.priv.gc.ca
                      </a>
                    </li>
                    <li>
                      <strong className="text-white">Phone:</strong>{" "}
                      1-800-282-1376
                    </li>
                    <li>
                      <strong className="text-white">Mail:</strong> 30 Victoria
                      Street, Gatineau, QC K1A 1H3
                    </li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  16. Consent
                </h2>
                <p className="text-gray-300">
                  By using Crypsey&apos;s services, you consent to the
                  collection, use, and disclosure of your information as
                  described in this Privacy Policy. You may withdraw consent at
                  any time, subject to legal and contractual restrictions.
                </p>
              </section>

              <div className="bg-cyan-500/10 border-l-4 border-cyan-500 rounded-lg p-6 mt-8">
                <h3 className="text-xl font-bold mb-4 text-white">
                  Key Takeaways
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    We never collect personally identifiable borrower
                    information
                  </li>
                  <li>
                    All mortgage data is fully anonymized before entering our
                    system
                  </li>
                  <li>We do not sell customer information to third parties</li>
                  <li>Your data is encrypted and securely stored</li>
                  <li>
                    You have full rights to access, correct, or delete your
                    information
                  </li>
                  <li>We comply with PIPEDA and Canadian privacy laws</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MortgageChainFooter
        onScheduleCall={() => emitFormEvent("call")}
        onViewDeck={() => emitFormEvent("deck")}
      />
    </div>
  );
}
